import { App, Button, Dropdown, Input, type MenuProps, Spin, Tag, Tooltip } from 'antd';
import clsx from 'clsx';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InterfaceLimitationEnum } from '../../../enums/InterfaceLimitationEnum';
import { useAuth } from '../../../hooks/useAuth';
import { useColorScheme } from '../../../hooks/useColorScheme/useColorScheme';
import { useTags } from '../../../hooks/useTags';
import type TagInterface from '../../../interfaces/Tag';
import tagColors from '../../../lib/tagColors';
import { useAppStore } from '../../../store/store';
import { getTagPresentationStyle } from '../../../utils/colorsUtilis';
import { getSettingTitle } from '../../../utils/settingSections';
import { hasInterfaceLimitation } from '../../../utils/utils';
import { Icon } from '../../Core/Icon';
import TruncatedTooltip from '../TruncatedTooltip';

const MAX_TAGS_AMOUNT = 100;
/** ponytail: sidebar list ceiling; raise if denser tag sets become common. */
const MAX_VISIBLE_TAGS = 10;

const TagsList = () => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const { authenticatedUser, isAdmin, isSuperAdmin } = useAuth();
  const { resolvedColorScheme } = useColorScheme();
  const isDarkMode = resolvedColorScheme === 'dark';
  const {
    tags,
    isLoadingTags,
    selectedTags,
    setSelectedTags,
    handleOpenTagForm,
    updateTag,
    handleDeleteTag,
  } = useTags();
  const [searchQuery, setSearchQuery] = useState('');
  const isExpanded = useAppStore((state) => state.appEvents.tagsList.isExpanded);
  const setIsExpanded = useAppStore((state) => state.appEvents.tagsList.setIsExpanded);

  const query = searchQuery.trim().toLowerCase();
  const filteredTags = query
    ? tags.filter((tag) => tag.title?.toLowerCase().includes(query))
    : tags;
  const showExpandToggle = filteredTags.length > MAX_VISIBLE_TAGS;
  const visibleTags =
    isExpanded || !showExpandToggle ? filteredTags : filteredTags.slice(0, MAX_VISIBLE_TAGS);

  /**
   * @description Handle the tag click
   * @param tag TagInterface
   */
  const handleTagClick = (tag: TagInterface) => {
    setSelectedTags((prevSelectedTags: TagInterface[]) => {
      if (prevSelectedTags.some((t: TagInterface) => t.id === tag.id)) {
        return prevSelectedTags.filter((t: TagInterface) => t.id !== tag.id);
      } else {
        return [...prevSelectedTags, tag];
      }
    });
  };

  /**
   * @description Get the dropdown items for a tag
   * @param tag TagInterface
   * @returns MenuProps['items']
   */
  const getDropdownItemsForTag = (tag: TagInterface): MenuProps['items'] => {
    let items: MenuProps['items'] = [
      {
        label: t('common.tagColor'),
        icon: <Icon name="SwatchBookIcon" className="h-5 w-5" />,
        key: 'color',
        children: tagColors
          .filter((color) => color.text !== tag.color)
          .map((color) => ({
            label: (
              <Tag className="w-full">
                <div className="flex flex-row items-center gap-2 py-1">
                  <div
                    className="h-4 w-4 rounded-md"
                    style={{
                      backgroundColor: color.text,
                    }}
                  ></div>
                  <span
                    className="text-xs"
                    style={{
                      color: color.text,
                    }}
                  >
                    {t('common.tagColor')}
                  </span>
                </div>
              </Tag>
            ),
            key: color.text,
            onClick: () => {
              updateTag({ ...tag, color: color.text });
            },
          })),
      },
      {
        type: 'divider',
      },
      {
        label: t('common.hideFromList'),
        icon: <Icon name="EyeOffIcon" className="h-5 w-5" />,
        key: 'hide',
        onClick: () => {
          updateTag({ ...tag, fileVisible: false });
        },
      },
      {
        label: t('common.showInList'),
        icon: <Icon name="EyeIcon" className="h-5 w-5" />,
        key: 'show',
        onClick: () => {
          updateTag({ ...tag, fileVisible: true });
        },
      },
      {
        type: 'divider',
      },
      {
        label: t('common.edit'),
        icon: <Icon name="PencilIcon" className="h-5 w-5" />,
        key: 'edit',
        onClick: () => {
          handleOpenTagForm(tag);
        },
      },
      // Access & Protections tab only exists in TagForm edit mode (tag.id)
      ...(tag.id
        ? [
            {
              label: t('common.editAccessAndProtections'),
              icon: <Icon name="ShieldCheckIcon" className="h-5 w-5" />,
              key: 'editAccess',
              onClick: () => {
                handleOpenTagForm(tag, { initialTab: 'access' });
              },
            },
          ]
        : []),
      {
        label: t('common.remove'),
        danger: true,
        icon: <Icon name="TrashIcon" className="h-5 w-5" />,
        key: 'remove',
        onClick: () => {
          handleDeleteTag(tag);
        },
      },
    ];

    // Check if the tag is fileVisible and remove the hide and show items
    if (tag.fileVisible) {
      items = items.filter((item) => item?.key !== 'show');
    } else {
      items = items.filter((item) => item?.key !== 'hide');
    }

    return items;
  };

  return (
    !hasInterfaceLimitation(
      authenticatedUser?.settings || [],
      InterfaceLimitationEnum.DenyShowTags,
    ) && (
      <div className="flex flex-col gap-2">
        <div className="flex flex-row items-center justify-center gap-2 pr-0.5 pl-3.5 lg:justify-between">
          <label className="text-blue-dark hidden font-medium lg:block">{t('common.tags')}</label>
          <div className="mr-0.5 flex w-full flex-row items-center gap-2 lg:w-auto">
            {!hasInterfaceLimitation(
              authenticatedUser?.settings || [],
              InterfaceLimitationEnum.DenyCompanyTag,
            ) && (
              <Tooltip title={t('common.addTag')}>
                <Button
                  type="text"
                  size="small"
                  aria-label={t('a11y.addTag')}
                  disabled={isLoadingTags}
                  onClick={() => {
                    if (tags.length >= MAX_TAGS_AMOUNT) {
                      notification.warning({
                        title: t('errors.tagsAmountLimitReached', { limit: MAX_TAGS_AMOUNT }),
                      });
                    } else {
                      handleOpenTagForm(null);
                    }
                  }}
                >
                  <Icon name="CirclePlusIcon" className="h-6 w-6 lg:h-4 lg:w-4" />
                </Button>
              </Tooltip>
            )}
          </div>
        </div>
        <Spin className="py-10" spinning={isLoadingTags}>
          <div className="flex flex-col">
            {tags?.length === 0 ? (
              <div className="hidden px-4 text-sm text-black lg:block">
                {isLoadingTags ? t('common.loading') : t('common.noTagsYet')}
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {/* Sidebar icon-only below lg (MainLayout isIconOnlySider / Sidebar w-[60px]). */}
                <div className="hidden px-3.5 lg:block">
                  <Input
                    allowClear
                    size="small"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={`${t('common.searchTags')}...`}
                    prefix={<Icon name="SearchIcon" className="text-gray-dark h-3.5 w-3.5" />}
                    aria-label={t('common.searchTags')}
                  />
                </div>
                <div className="flex flex-col">
                  {filteredTags.length === 0 ? (
                    <div className="px-3.5 text-sm text-black">{t('common.noResults')}</div>
                  ) : (
                    visibleTags.map((tag) => {
                      const isSelected = selectedTags.some(
                        (selectedTag) => selectedTag.id === tag.id,
                      );

                      return (
                        <div
                          key={tag.id}
                          className="flex w-full cursor-pointer items-center px-3.5 lg:justify-between"
                        >
                          <Button
                            type="text"
                            aria-pressed={isSelected}
                            aria-label={t('a11y.tagToggle', {
                              name: tag.title,
                              state: isSelected ? t('a11y.tagSelected') : t('a11y.tagNotSelected'),
                            })}
                            className={clsx('fle! min-w-0! flex-1! items-center! px-0!')}
                            key={tag.id}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (!isLoadingTags) {
                                handleTagClick(tag);
                              }
                            }}
                          >
                            <Tooltip
                              title={
                                tag.usedInSettings?.length
                                  ? `${t('modal.tagForm.usedInSettings')} ${tag.usedInSettings
                                      .map((settingType) => getSettingTitle(settingType, t))
                                      .join(', ')}`
                                  : undefined
                              }
                            >
                              <Tag
                                className={clsx(
                                  'relative w-full transition-all duration-300',
                                  !tag.fileVisible &&
                                    'bg-diagonal-stripes pointer-events-none! opacity-50! mix-blend-multiply!',
                                  isSelected &&
                                    'ring-blue-dark ring-offset-background ring-2 ring-offset-2',
                                )}
                                style={getTagPresentationStyle(tag.color, {
                                  darkMode: isDarkMode,
                                })}
                              >
                                <div className="flex w-full flex-row items-center justify-center lg:justify-between">
                                  <div />
                                  <span
                                    className={clsx(
                                      'hidden max-w-28 text-center lg:block',
                                      isSelected && 'font-semibold',
                                    )}
                                  >
                                    <TruncatedTooltip>{tag.title}</TruncatedTooltip>
                                  </span>
                                  <span
                                    className={clsx('lg:hidden', isSelected && 'font-semibold')}
                                  >
                                    <TruncatedTooltip>{tag.title?.slice(0, 1)}</TruncatedTooltip>
                                  </span>
                                  <div className="hidden flex-row items-center gap-1 lg:flex">
                                    {tag?.isUsedForFileVisibility ? (
                                      <Tooltip title={t('common.tagIsUsedForFileVisibility')}>
                                        <div>
                                          <Icon
                                            name="Share2Icon"
                                            className="text-gray-dark h-3 w-3"
                                          />
                                        </div>
                                      </Tooltip>
                                    ) : (
                                      <div className="w-1" />
                                    )}
                                    {/* Public tag indicator icon */}
                                    {tag.publicTag ? (
                                      <Icon
                                        name="BuildingIcon"
                                        className="text-gray-dark h-3 w-3"
                                      />
                                    ) : (
                                      <div className="w-1" />
                                    )}
                                  </div>
                                </div>
                              </Tag>
                            </Tooltip>
                          </Button>
                          {(isAdmin || isSuperAdmin) && !tag.isSystemTag && (
                            <Dropdown
                              trigger={['click']}
                              menu={{
                                items: getDropdownItemsForTag(tag),
                              }}
                            >
                              <Button
                                type="text"
                                size="small"
                                aria-label={t('a11y.tagActions', { name: tag.title })}
                                className="text-gray-dark p-0!"
                              >
                                <Icon name="EllipsisVerticalIcon" className="h-4 w-4" />
                              </Button>
                            </Dropdown>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
                {showExpandToggle && (
                  <div className="border-gray-medium flex items-center justify-center border-t">
                    {/* Icon-only below lg (same as search / tag titles); label via tooltip. */}
                    <Tooltip title={isExpanded ? t('common.collapse') : t('common.showMore')}>
                      <Button
                        type="link"
                        size="small"
                        className="text-blue-dark! text-xxs! h-auto! min-h-0! gap-1! px-2! py-1!"
                        aria-label={isExpanded ? t('common.collapse') : t('common.showMore')}
                        onClick={() => setIsExpanded(!isExpanded)}
                      >
                        <Icon
                          name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                          className="h-3 w-3"
                        />
                        <span className="hidden lg:inline">
                          {isExpanded ? t('common.collapse') : t('common.showMore')}
                        </span>
                      </Button>
                    </Tooltip>
                  </div>
                )}
              </div>
            )}
          </div>
        </Spin>
      </div>
    )
  );
};

export default TagsList;
